import {
  Button,
  CircularProgress,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useAlert } from "react-alert";
import {
  Question,
  useAddDraftSummaryMutation,
} from "../../../redux/Hire/interviewQuestionSlice";
import QuestionView from "./Question";
import TabPanel from "./TabPanel";

type Props = {
  questions: Array<Question>;
  interviewId?: string;
  meetingId?: string;
  hideSubmit?: boolean;
};

const QuestionsList = ({
  questions,
  interviewId,
  meetingId,
  hideSubmit = false,
}: Props) => {
  const [addDraftSummary, { isLoading: isDraftSummaryLoading, isSuccess }] =
    useAddDraftSummaryMutation();

  const alert = useAlert();
  // Group questions by subject
  const questionsBySubject = useMemo(() => {
    const map: Record<string, Question[]> = {};
    questions.forEach((q) => {
      const subject = q.subject || "General";
      if (!map[subject]) map[subject] = [];
      map[subject].push(q);
    });
    return map;
  }, [questions]);

  const subjects = Object.keys(questionsBySubject);
  const [tab, setTab] = useState(0);

  const submitForm = useCallback(async () => {
    if (interviewId && meetingId) {
      try {
        await addDraftSummary({
          interviewId: interviewId,
          meetingId: meetingId,
        }).unwrap();
      } catch (e: any) {
        alert.error("Error updating candidate summary " + e.message);
      }
    }
  }, [addDraftSummary, alert, interviewId, meetingId]);

  if (subjects.length === 0) {
    return (
      <Grid container justifyContent="center" alignItems="center" height="100%">
        <Typography fontSize="14px" lineHeight={"20px"} color={"#98A2B3"}>
          No questions available
        </Typography>
      </Grid>
    );
  }

  return (
    <Grid
      container
      direction="column"
      sx={{ width: "100%", minHeight: "350px" }}
    >
      <Grid item>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2 }}
        >
          {subjects.map((subject, idx) => (
            <Tab label={subject} key={subject} />
          ))}
        </Tabs>
      </Grid>
      {subjects.map((subject, idx) => (
        <TabPanel
          key={subject}
          tabValue={tab}
          index={idx}
          id={`questions-tabpanel-${idx}`}
          aria-labelledby={`questions-tab-${idx}`}
        >
          <Grid container direction={"column"} gap={2}>
            {questionsBySubject[subject].map((question, qIdx) => (
              <Grid item key={question._id}>
                <QuestionView
                  question={question}
                  index={qIdx}
                  key={question._id}
                  interviewId={interviewId}
                />
              </Grid>
            ))}
          </Grid>
        </TabPanel>
      ))}
      {!hideSubmit && (
        <Grid item>
          <Button
            color="primary"
            variant="contained"
            disabled={isDraftSummaryLoading || isSuccess}
            onClick={submitForm}
          >
            {isDraftSummaryLoading ? (
              <CircularProgress size={20}></CircularProgress>
            ) : (
              "Submit answers"
            )}
          </Button>
        </Grid>
      )}
    </Grid>
  );
};

export default QuestionsList;
